import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Input,
  ModalHeader,
  ModalBody,
  Label,
  ModalFooter,
  Modal,
  Form,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  FormFeedback,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Table,
  Button,
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import * as moment from "moment";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { isEmpty } from "lodash";

// Import Images
import dummyImg from "../../../assets/images/users/user-dummy-img.jpg";

//Import actions
import {
  getLeads as onGetLeads,
  getLeadFilters as onGetLeadFilters,
  addNewLead as onAddNewLead,
  updateLead as onUpdateLead,
  deleteLead as onDeleteLead,
} from "../../../slices/thunks";
//redux
import { useSelector, useDispatch } from "react-redux";
import TableContainer from "../../../Components/Common/TableContainer";
import DeleteModal from "../../../Components/Common/DeleteModal";
import CrmFilter from "./CrmFilter";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import Loader from "../../../Components/Common/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createSelector } from "reselect";

const CrmLeads = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [show, setShow] = useState(false);

  // Current logged-in CRM user
  // =====================================================
  // USER-SPECIFIC LEAD FILTER PERSISTENCE
  // =====================================================

  // Try to get the logged-in user from your existing auth storage
  let authUser = {};

  try {
    authUser = JSON.parse(
      localStorage.getItem("authUser") || "{}"
    );
  } catch (error) {
    console.error("Unable to parse authUser:", error);
  }

  // Support different possible user ID fields
  const crmUserId =
    authUser?.id ||
    authUser?.userid ||
    authUser?.userId ||
    authUser?.user_id ||
    localStorage.getItem("userId") ||
    localStorage.getItem("userid") ||
    localStorage.getItem("user_id");

  // Support username as fallback
  const crmUsername =
    authUser?.username ||
    authUser?.user_name ||
    authUser?.userName ||
    authUser?.email ||
    localStorage.getItem("username") ||
    localStorage.getItem("userName");

  const crmDisplayName =
    authUser?.name ||
    authUser?.display_name ||
    authUser?.displayName ||
    authUser?.full_name ||
    authUser?.first_name ||
    crmUsername ||
    "Unknown User";

  // Create a stable user-specific key
  const filterUserKey =
    crmUserId ||
    crmUsername ||
    "unknown-user";

  const savedFilterKey =
    `crm_leads_filter_${String(filterUserKey)}`;

  // =====================================================
  // RESTORE SAVED FILTER
  // =====================================================

  const getSavedCvid = () => {
    const saved = localStorage.getItem(savedFilterKey);

    console.log(
      "RESTORING LEAD FILTER:",
      {
        savedFilterKey,
        saved,
        crmUserId,
        crmUsername,
      }
    );

    if (!saved) {
      return null;
    }

    const cvid = Number(saved);

    return Number.isFinite(cvid) && cvid > 0
      ? cvid
      : null;
  };

  const initialSavedCvid = getSavedCvid();

  const [selectedCvid, setSelectedCvid] =
    useState(initialSavedCvid);

  const [appliedCvid, setAppliedCvid] =
    useState(initialSavedCvid);

  

  

  const selectLayoutState = (state) => state.Crm;
  const crmleadsProperties = createSelector(
    selectLayoutState,
    (state) => ({
      leads: state.leads || [],

      leadsTotal:
        Number(state.leadsTotal || 0),

      leadsPage:
        Number(state.leadsPage || 1),

      leadsPageLimit:
        Number(state.leadsPageLimit || 25),

      leadFilters:
      Array.isArray(state.leadFilters)
          ? state.leadFilters
          : [],

      selectedCvid:
        state.selectedCvid || null,

      isLeadsSuccess:
        state.isLeadsSuccess,

      error:
        state.error,
    })
  );
  // Inside your component
  const {
    leads,
    leadsTotal,
    leadsPage,
    leadsPageLimit,
    leadFilters,
    leadColumns,
    leadFilterName,
    isLeadsSuccess,
    error,
  } = useSelector(crmleadsProperties);
  const totalLeads = leadsTotal;
  const [leadPage, setLeadPage] = useState(1);
  const leadPageSize = 25;
  const [leadSearch, setLeadSearch] = useState("");
  const [debouncedLeadSearch, setDebouncedLeadSearch] = useState("");
  const [localUpdatedLeads, setLocalUpdatedLeads] = useState({});
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedLeadSearch(leadSearch);
    }, 400);

    return () => clearTimeout(timer);
  }, [leadSearch]);
  const leadItems = useMemo(() => {
    const baseLeads = Array.isArray(leads) ? leads : [];

    const mergedLeads = baseLeads.map((item) => {
      const id = item.id || item._id;

      if (localUpdatedLeads[id]) {
        return {
          ...item,
          ...localUpdatedLeads[id],
        };
      }

      return item;
    });

    // Keep updated lead visible even if CRM custom view
    // temporarily removes it from the API response.
    Object.entries(localUpdatedLeads).forEach(
      ([id, updatedLead]) => {
        const alreadyExists = mergedLeads.some(
          (item) =>
            String(item.id || item._id) === String(id)
        );

        if (!alreadyExists) {
          mergedLeads.push(updatedLead);
        }
      }
    );

    return mergedLeads;
  }, [leads, localUpdatedLeads]);

  const newLeadsCount = leadItems.filter(
    (item) =>
      Array.isArray(item.tags) &&
      item.tags.includes("New")
  ).length;

  const qualifiedLeadsCount = leadItems.filter(
    (item) => Number(item.score) >= 70
  ).length;

  const lostLeadsCount = leadItems.filter(
    (item) =>
      Number(item.score) > 0 &&
      Number(item.score) <= 35
  ).length;
  
  useEffect(() => {
    dispatch(
      onGetLeads({
        page: leadPage,
        pageLimit: leadPageSize,
        search: debouncedLeadSearch,
        order: "ASC",
        orderBy: "",
        cvid: appliedCvid,
      })
    );
  }, [
    dispatch,
    leadPage,
    leadPageSize,
    debouncedLeadSearch,
    appliedCvid,
  ]);

  useEffect(() => {
    // If current page has no records but we are not on page 1,
    // automatically go back to the previous page.
    if (
      isLeadsSuccess &&
      leadPage > 1 &&
      Array.isArray(leadItems) &&
      leadItems.length === 0
    ) {
      console.log(
        "Empty lead page detected. Going back from page:",
        leadPage
      );

      setLeadPage((prev) => Math.max(1, prev - 1));
    }
  }, [
    isLeadsSuccess,
    leadItems.length,
    leadPage,
  ]);

  useEffect(() => {
    setLead(leads);
  }, [leads]);




  useEffect(() => {
    if (!isEmpty(leads)) {
      setLead(leads);
      setIsEdit(false);
    }
  }, [leads]);

  const [isEdit, setIsEdit] = useState(false);
  const [lead, setLead] = useState([]);

  const [leadStatuses, setLeadStatuses] = useState([]);

  useEffect(() => {
    const loadLeadStatuses = async () => {
      try {
        const result = await dispatch(onGetLeadFilters()).unwrap();

        console.log("CRM STATUS OPTIONS:", result?.leadStatuses);

        setLeadStatuses(
          Array.isArray(result?.leadStatuses)
            ? result.leadStatuses
            : []
        );
      } catch (error) {
        console.error(
          "Failed to load CRM lead statuses:",
          error
        );

        setLeadStatuses([]);
      }
    };

    loadLeadStatuses();
  }, [dispatch]);

  

  //delete lead
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);

  const [modal, setModal] = useState(false);

  const [isInfoDetails, setIsInfoDetails] = useState(false);

  // Lead Overview (View action) panel
  const [info, setInfo] = useState({});
  const [isOverview, setIsOverview] = useState(false);
  const toggleOverview = useCallback(() => {
    setIsOverview((prev) => !prev);
  }, []);
  const handleLeadView = (leadData, shouldNavigate = true) => {
    if (!leadData) return;

    

    const firstName = leadData.firstname || "";
    const lastName = leadData.lastname || "";

    const fullName =
      `${firstName} ${lastName}`.trim() ||
      leadData.company ||
      "Unnamed Lead";

    const normalizedLead = {
      ...leadData,

      id: leadData.id || leadData._id || "",
      _id: leadData._id || leadData.id || "",

      name: fullName,
      firstname: firstName,
      lastname: lastName,

      company: leadData.company || "",
      email: leadData.email || "",
      phone: leadData.phone || "",

      // COURSE
      course:
        leadData.course ||
        leadData.cf_907 ||
        "",

      // LOCATION
      location:
        leadData.location ||
        leadData.lane ||
        leadData.address ||
        "",

      // Keep original fields also
      cf_907: leadData.cf_907 || leadData.course || "",
      lane:
        leadData.lane ||
        leadData.location ||
        leadData.address ||
        "",

      status:
        leadData.leadstatus ||
        leadData.status ||
        "",

      leadstatus:
        leadData.leadstatus ||
        leadData.status ||
        "",

      assignedTo:
        leadData.assigned_user_name ||
        leadData.assigned_user ||
        leadData.assignedto ||
        leadData.assigned_to ||
        leadData.assigned_user_id ||
        "-",

      score: leadData.score || "",

      date:
        leadData.createdtime ||
        leadData.date ||
        "",
    };

    setInfo(normalizedLead);
    setIsOverview(true);

    if (shouldNavigate) {
      navigate(
        `/apps-crm-leads?view=${encodeURIComponent(
          normalizedLead.id
        )}`
      );
    }
  };

  useEffect(() => {

    const editId = searchParams.get("edit");
    const viewId = searchParams.get("view");
    const filter = searchParams.get("filter");
    const create = searchParams.get("create");

    // ============================
    // EDIT
    // ============================

    if (editId) {

      const foundLead = leadItems.find(
        (item) =>
          String(item.id || item._id) === String(editId)
      );

      if (foundLead) {

        const firstName = foundLead.firstname || "";
        const lastName = foundLead.lastname || "";

        const normalizedLead = {
          ...foundLead,

          id: foundLead.id || foundLead._id || "",
          _id: foundLead.id || foundLead._id || "",

          name:
            `${firstName} ${lastName}`.trim() ||
            foundLead.company ||
            "",

          company: foundLead.company || "",
          email: foundLead.email || "",
          phone: foundLead.phone || "",

          location:
            foundLead.lane ||
            foundLead.location ||
            "",

          course:
            foundLead.course ||
            foundLead.cf_907 ||
            "",

          leadstatus:
            foundLead.leadstatus ||
            foundLead.status ||
            "",

          status:
            foundLead.leadstatus ||
            foundLead.status ||
            "",

          assigned_user_id:
            foundLead.assigned_user_id || "",

          tags: Array.isArray(foundLead.tags)
            ? foundLead.tags
            : [],

          date:
            foundLead.createdtime ||
            foundLead.date ||
            "",
        };

        setLead(normalizedLead);
        setIsEdit(true);
        setModal(true);

      }

      setIsOverview(false);
      setShow(false);

      return;
    }


    // ============================
    // VIEW
    // ============================

    if (viewId) {

      const foundLead = leadItems.find(
        (item) =>
          String(item.id || item._id) === String(viewId)
      );

      if (foundLead) {
        handleLeadView(foundLead, false);
      }

      setModal(false);
      setIsEdit(false);
      setShow(false);

      return;
    }


    // ============================
    // FILTER
    // ============================

    if (filter === "open") {

      setShow(true);

      setModal(false);
      setIsEdit(false);
      setIsOverview(false);

      return;
    }


    // ============================
    // CREATE
    // ============================

    if (create === "open") {

      setIsEdit(false);
      setLead(null);
      setDate(defaultdate());
      setTag([]);
      setAssignTag([]);
      setModal(true);

      setShow(false);
      setIsOverview(false);

      return;
    }


    // ============================
    // NO UI STATE
    // ============================

    setModal(false);
    setIsEdit(false);
    setIsOverview(false);
    setShow(false);

  }, [
    searchParams,
    leadItems
  ]);

  // ========================================
  // MOBILE LEAD CARD
  // ========================================
  const MobileLeadCard = ({ leadData }) => {

    const firstName = leadData.firstname || "";
    const lastName = leadData.lastname || "";

    const fullName =
      `${firstName} ${lastName}`.trim() ||
      leadData.company ||
      "Unnamed Lead";

    const phone = leadData.phone || "";
    const email = leadData.email || "";
    const company = leadData.company || "";
    const location = leadData.lane || leadData.location || "";
    const course = leadData.course || leadData.cf_907 || "";
    const status = leadData.leadstatus || leadData.status || "";

    const normalizedStatus = String(status).trim().toLowerCase();

    const isClosedOrLost =
      normalizedStatus === "closed" ||
      normalizedStatus === "lost" ||
      normalizedStatus.includes("closed") ||
      normalizedStatus.includes("lost");

    return (
      <div className="crm-mobile-lead-card">

        {/* HEADER */}
        <div className="crm-mobile-lead-header">

          <div
            className="crm-mobile-lead-user"
            onClick={() => handleLeadView(leadData)}
          >

            <div className="crm-mobile-avatar">
              {fullName.charAt(0).toUpperCase()}
            </div>

            <div>

              <div className="crm-mobile-lead-name">
                {fullName}
              </div>

              <div className="crm-mobile-company">
                {company || "No company"}
              </div>

            </div>

          </div>

          <button
            type="button"
            className="crm-mobile-more"
            onClick={() => handleLeadView(leadData)}
          >
            <i className="ri-more-2-fill"></i>
          </button>

        </div>


        {/* INFORMATION */}
        {/* INFORMATION */}
        <div className="crm-mobile-lead-info">

          {/* COURSE */}
          {course && (
            <div className="crm-mobile-info-row">
              <i className="ri-graduation-cap-line"></i>

              <span className="crm-mobile-info-value">
                {course}
              </span>
            </div>
          )}

          {/* EMAIL */}
          {email && (
            <div className="crm-mobile-info-row">
              <i className="ri-mail-line"></i>

              <span className="crm-mobile-info-value">
                {email}
              </span>
            </div>
          )}

          {/* LOCATION */}
          {location && (
            <div className="crm-mobile-info-row">
              <i className="ri-map-pin-line"></i>

              <span className="crm-mobile-info-value">
                {location}
              </span>
            </div>
          )}

          {/* STATUS */}
          <div className="crm-mobile-status-row">
            {status ? (
              <span
                className={`crm-mobile-status-badge ${isClosedOrLost
                    ? "crm-mobile-status-danger"
                    : "crm-mobile-status-success"
                  }`}
              >
                <span className="crm-mobile-status-dot"></span>
                {status}
              </span>
            ) : (
              <button
                type="button"
                className="crm-mobile-status-update"
                onClick={() => handleLeadClick(leadData)}
              >
                <i className="ri-edit-2-line"></i>
                Update Status
              </button>
            )}
          </div>

        </div>


        {/* ACTIONS */}
        {/* MOBILE ACTIONS */}
        <div className="crm-mobile-actions">

          {/* CALL */}
          {phone && (
            <a
              href={`tel:${phone}`}
              className="crm-mobile-icon-action crm-mobile-call"
              title="Call"
              aria-label="Call"
            >
              <i className="ri-phone-line"></i>
            </a>
          )}

          {/* WHATSAPP */}
          {phone && (
            <button
              type="button"
              className="crm-mobile-icon-action crm-mobile-whatsapp"
              title="WhatsApp"
              aria-label="WhatsApp"
              onClick={() => {
                window.open(
                  `https://wa.me/${phone.replace(/\D/g, "")}`,
                  "_blank"
                );
              }}
            >
              <i className="ri-whatsapp-line"></i>
            </button>
          )}

          {/* EMAIL */}
          {email && (
            <a
              href={`mailto:${email}`}
              className="crm-mobile-icon-action crm-mobile-email"
              title="Email"
              aria-label="Email"
            >
              <i className="ri-mail-line"></i>
            </a>
          )}

          {/* VIEW */}
          <button
            type="button"
            className="crm-mobile-icon-action crm-mobile-view"
            title="View"
            aria-label="View"
            onClick={() => handleLeadView(leadData)}
          >
            <i className="ri-eye-line"></i>
          </button>

          {/* EDIT */}
          <button
            type="button"
            className="crm-mobile-icon-action crm-mobile-edit"
            title="Edit"
            aria-label="Edit"
            onClick={() => handleLeadClick(leadData)}
          >
            <i className="ri-pencil-line"></i>
          </button>

        </div>

      </div>
    );
  };

  const MobileLeadPagination = () => {
    const totalPages = Math.max(
      1,
      Math.ceil(Number(leadsTotal || 0) / Number(leadPageSize || 25))
    );

    // Never allow the UI to stay beyond the last valid page
    useEffect(() => {
      if (leadPage > totalPages) {
        setLeadPage(totalPages);
      }
    }, [leadPage, totalPages]);

    if (totalPages <= 1) {
      return null;
    }

    const handlePrevious = () => {
      setLeadPage((prev) => Math.max(1, prev - 1));
    };

    const handleNext = () => {
      setLeadPage((prev) =>
        Math.min(totalPages, prev + 1)
      );
    };

    return (
      <div className="crm-mobile-pagination">

        <button
          type="button"
          className="crm-mobile-pagination-btn"
          disabled={leadPage <= 1}
          onClick={handlePrevious}
          aria-label="Previous page"
        >
          <i className="ri-arrow-left-s-line"></i>
        </button>

        <div className="crm-mobile-page-info">
          <span className="crm-mobile-current-page">
            {leadPage}
          </span>

          <span className="crm-mobile-page-separator">
            /
          </span>

          <span>
            {totalPages}
          </span>
        </div>

        <button
          type="button"
          className="crm-mobile-pagination-btn"
          disabled={leadPage >= totalPages}
          onClick={handleNext}
          aria-label="Next page"
        >
          <i className="ri-arrow-right-s-line"></i>
        </button>

      </div>
    );
  };


  const [tag, setTag] = useState([]);
  const [assignTag, setAssignTag] = useState([]);

  const handlestag = (tags) => {
    setTag(tags);
    const assigned = tags.map((item) => item.value);
    setAssignTag(assigned);
  };

  const tags = [
    { label: "Exiting", value: "Exiting" },
    { label: "Lead", value: "Lead" },
    { label: "Long-term", value: "Long-term" },
    { label: "Partner", value: "Partner" }
  ];

  const toggle = useCallback(() => {

    if (modal) {

      setModal(false);
      setLead(null);
      setIsEdit(false);

      // Remove UI state from URL
      navigate("/apps-crm-leads");

    } else {

      setModal(true);
      setDate(defaultdate());
      setTag([]);
      setAssignTag([]);

    }

  }, [modal, navigate]);

  // Delete Data
  const handleDeleteLead = () => {
    if (lead) {
      dispatch(onDeleteLead(lead._id));
      setDeleteModal(false);
    }
  };

  

  const onClickDelete = (lead) => {
    setLead(lead);
    setDeleteModal(true);
  };

  const handleApplyFilter = (cvid) => {
    const normalizedCvid =
      cvid !== null &&
        cvid !== undefined &&
        cvid !== ""
        ? Number(cvid)
        : null;

    console.log("=================================");
    console.log("FILTER SELECTED");
    console.log("CVID:", normalizedCvid);
    console.log("USER ID:", crmUserId);
    console.log("USERNAME:", crmUsername);
    console.log("=================================");

    // Update both states
    setSelectedCvid(normalizedCvid);
    setAppliedCvid(normalizedCvid);

    // Always start from page 1
    setLeadPage(1);

    // Close filter panel
    setShow(false);

    if (normalizedCvid) {

      navigate(
        `/apps-crm-leads?filter=${encodeURIComponent(
          normalizedCvid
        )}`
      );

    } else {

      navigate("/apps-crm-leads");

    }

    // Save permanently for this user
    if (normalizedCvid) {
      localStorage.setItem(
        savedFilterKey,
        String(normalizedCvid)
      );

      console.log(
        "FILTER SAVED:",
        localStorage.getItem(savedFilterKey)
      );
    } else {
      localStorage.removeItem(savedFilterKey);

      console.log("FILTER CLEARED");
    }
  };

  const handleClearFilter = () => {

    setSelectedCvid(null);
    setAppliedCvid(null);

    setLeadPage(1);
    setShow(false);

    // Forget user's previously selected filter
    if (savedFilterKey) {
      localStorage.removeItem(savedFilterKey);
    }
  };

  // Add Data
  const handleLeadClicks = useCallback((arg) => {

    const leadData = arg || {};

    const firstName = leadData.firstname || "";
    const lastName = leadData.lastname || "";

    const fullName =
      `${firstName} ${lastName}`.trim() ||
      leadData.company ||
      "Unnamed Lead";

    const normalizedLead = {
      ...leadData,

      // Vtiger record ID
      id: leadData.id || "",
      _id: leadData.id || leadData._id || "",

      // Name
      name: fullName,
      firstname: firstName,
      lastname: lastName,

      // Basic fields
      company: leadData.company || "",
      email: leadData.email || "",
      phone: leadData.phone || "",

      // Vtiger custom field
      course: leadData.cf_907 || "",

      // Location
      location:
        leadData.lane ||
        leadData.location ||
        "",

      // Status
      leadstatus:
        leadData.leadstatus ||
        leadData.status ||
        "",

      status:
        leadData.leadstatus ||
        leadData.status ||
        "",

      // Assigned user
      // Assigned User
      assigned_user_id:
        leadData.assigned_user_id ||
        leadData.smownerid ||
        "",

      assignedTo:
        leadData.assigned_user_name ||
        leadData.assigned_user ||
        leadData.assignedto ||
        leadData.assigned_to ||
        leadData.smownerid ||
        "-",

      // Score
      score: leadData.score || 0,

      // Tags
      tags: Array.isArray(leadData.tags)
        ? leadData.tags
        : [],

      // Date
      date:
        leadData.createdtime ||
        leadData.date ||
        "",
    };

    console.log("EDIT LEAD DATA:", normalizedLead);

    setLead(normalizedLead);
    setIsEdit(true);
    toggle();

  }, [toggle]);

  const toggleInfo = () => {
    setIsInfoDetails(!isInfoDetails);
  };

  const handleLeadFilterChange = (selectedOption) => {

    const cvid = selectedOption
      ? Number(selectedOption.value)
      : null;

    setSelectedCvid(cvid);

    // Always return to first page
    setLeadPage(1);

    setIsInfoDetails(false);

    // Save selected filter for this CRM user
    if (savedFilterKey) {

      if (cvid) {
        localStorage.setItem(
          savedFilterKey,
          String(cvid)
        );

        console.log(
          "Saved lead filter:",
          cvid,
          "for user:",
          crmUserId
        );

      } else {
        localStorage.removeItem(savedFilterKey);
      }
    }
  };

  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      name: (lead && lead.name) || '',
      company: (lead && lead.company) || '',
      email: (lead && lead.email) || '',
      phone: (lead && lead.phone) || '',

      location:
        (lead && (lead.location || lead.lane)) || '',

      course:
        (lead && (lead.course || lead.cf_907)) || '',

      leadstatus:
        (lead && (lead.leadstatus || lead.status)) || '',

      assigned_user_id:
        (lead && lead.assigned_user_id) || '',

      date:
        (lead && (lead.date || lead.createdtime)) || '',

      tags:
        (lead && lead.tags) || [],
    },
    validationSchema: Yup.object({
      name: isEdit
        ? Yup.string().notRequired()
        : Yup.string().required("Please Enter Name"),

      company: Yup.string().notRequired(),

      email: Yup.string()
        .email("Please Enter Valid Email")
        .notRequired(),

      phone: isEdit
        ? Yup.string().notRequired()
        : Yup.string().required("Please Enter Phone"),

      location: isEdit
        ? Yup.string().notRequired()
        : Yup.string().required("Please Enter Location"),

      course: Yup.string().notRequired(),

      leadstatus: Yup.string().notRequired(),

      assigned_user_id: Yup.string().notRequired(),

      date: Yup.string().notRequired(),

      tags: Yup.mixed().notRequired(),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        const updateLead = {
          id: lead?.id || lead?._id || "",
          _id: lead?._id || lead?.id || "",

          name: values.name,
          company: values.company,
          score: values.score,
          phone: values.phone,

          location: values.location,

          course: values.course,

          // CRM Vtiger field
          leadstatus: values.leadstatus,

          assigned_user_id: values.assigned_user_id,

          date: values.date || date,

          tags: assignTag,
        };

        console.log("UPDATING CRM LEAD:", updateLead);

        if (!updateLead.id) {
          toast.error("CRM Lead ID is missing");
          return;
        }

        dispatch(onUpdateLead(updateLead));

        // Keep the updated lead visible in the current portal list
        const nameParts = (values.name || "").trim().split(/\s+/);

        const locallyUpdatedLead = {
          ...lead,

          id: updateLead.id,
          _id: updateLead._id,

          name: values.name,
          firstname: nameParts[0] || "",
          lastname: nameParts.slice(1).join(" ") || "",

          company: values.company,
          score: values.score,
          phone: values.phone,

          location: values.location,
          lane: values.location,

          course: values.course,
          cf_907: values.course,

          // Updated CRM status
          leadstatus: values.leadstatus,
          status: values.leadstatus,

          assigned_user_id: values.assigned_user_id,

          date: values.date || date,

          tags: assignTag,
        };

        setLocalUpdatedLeads((prev) => ({
          ...prev,
          [updateLead.id]: locallyUpdatedLead,
        }));

        console.log("PORTAL LOCAL LEAD UPDATED:", locallyUpdatedLead);

        toast.success("Lead Updated Successfully");

        validation.resetForm();

        setModal(false);
      } else {

        const newLead = {
          name: values.name,
          company: values.company,
          email: values.email,
          phone: values.phone,

          location: values.location,

          course: values.course,

          leadstatus: values.leadstatus,

          assigned_user_id: values.assigned_user_id,

          date: values.date || date,

          tags: assignTag,
        };

        console.log("ADDING CRM LEAD:", newLead);

        dispatch(onAddNewLead(newLead))
          .unwrap()
          .then(() => {
            validation.resetForm();
            setModal(false);

            dispatch(
              onGetLeads({
                page: 1,
                pageLimit: leadPageSize,
                search: "",
                order: "ASC",
                orderBy: "",
                cvid: appliedCvid,
              })
            );
          })
          .catch((err) => {
            console.error(err);
          });

        setModal(false);

        // Refresh leads from CRM
        setLeadPage(1);

        setTimeout(() => {
          dispatch(
            onGetLeads({
              page: 1,
              pageLimit: leadPageSize,
              search: debouncedLeadSearch,
              order: "ASC",
              orderBy: "",
              cvid: appliedCvid,
            })
          );
        }, 500);
      }
      toggle();
    },
  });

  // Update Data
  const handleLeadClick = useCallback((leadData) => {

    const leadRecord = leadData || {};

    const fullName =
      `${leadRecord.firstname || ""} ${leadRecord.lastname || ""}`.trim() ||
      leadRecord.company ||
      "";

    const normalizedLead = {
      ...leadRecord,

      id: leadRecord.id || leadRecord._id || "",
      _id: leadRecord.id || leadRecord._id || "",

      name: fullName,

      company: leadRecord.company || "",

      email: leadRecord.email || "",

      phone: leadRecord.phone || "",

      location:
        leadRecord.lane ||
        leadRecord.location ||
        "",

      course:
        leadRecord.course ||
        leadRecord.cf_907 ||
        "",

      leadstatus:
        leadRecord.leadstatus ||
        leadRecord.status ||
        "",

      status:
        leadRecord.leadstatus ||
        leadRecord.status ||
        "",

      assigned_user_id:
        leadRecord.assigned_user_id ||
        "",

      score: leadRecord.score || 0,

      tags: Array.isArray(leadRecord.tags)
        ? leadRecord.tags
        : [],

      date:
        leadRecord.createdtime ||
        leadRecord.date ||
        "",
    };

    setLead(normalizedLead);
    setIsEdit(true);

    // IMPORTANT:
    // Create browser history entry for Edit
    navigate(
      `/apps-crm-leads?edit=${encodeURIComponent(
        normalizedLead.id
      )}`
    );

    toggle();

  }, [toggle, navigate]);

  // Node API 
  // useEffect(() => {
  //   if (isLeadCreated) {
  //     setLead(null);
  //     dispatch(onGetLeads());
  //   }
  // }, [
  //   dispatch,
  //   isLeadCreated,
  // ]);

  const handleValidDate = (date) => {

    if (!date) {
      return "-";
    }

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return "-";
    }

    return moment(parsedDate).format("DD MMM YYYY");
  };

  // Checked All
  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".leadsCheckBox");

    if (checkall.checked) {
      ele.forEach((ele) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
    deleteCheckbox();
  }, []);

  // Delete Multiple
  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);

  const deleteMultiple = () => {
    const checkall = document.getElementById("checkBoxAll");
    selectedCheckBoxDelete.forEach((element) => {
      dispatch(onDeleteLead(element.value));
      setTimeout(() => { toast.clearWaitingQueue(); }, 3000);
    });
    setIsMultiDeleteButton(false);
    checkall.checked = false;
  };

  const deleteCheckbox = () => {
    const ele = document.querySelectorAll(".leadsCheckBox:checked");
    ele.length > 0 ? setIsMultiDeleteButton(true) : setIsMultiDeleteButton(false);
    setSelectedCheckBoxDelete(ele);
  };

  // Column
  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            className="form-check-input"
            id="checkBoxAll"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell) => {
          return (
            <input
              type="checkbox"
              className="leadsCheckBox form-check-input"
              value={cell.row.original.id}
              onChange={() => deleteCheckbox()}
            />
          );
        },
        id: "#",
        enableSorting: false,
      },

      // NAME
      {
        header: "Name",
        accessorKey: "lastname",
        enableColumnFilter: false,
        cell: (cell) => {

          const lead = cell.row.original;

          const firstName = lead.firstname || "";
          const lastName = lead.lastname || "";

          const fullName =
            `${firstName} ${lastName}`.trim() ||
            lead.company ||
            "Unnamed Lead";

          return (
            <div
              className="d-flex align-items-center cursor-pointer"
              onClick={() => handleLeadView(lead)}
            >
              <div className="flex-shrink-0">
                <div
                  className="avatar-xxs rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center"
                  style={{
                    width: "28px",
                    height: "28px",
                    fontSize: "12px",
                    fontWeight: "600"
                  }}
                >
                  {fullName.charAt(0).toUpperCase()}
                </div>
              </div>

              <div className="flex-grow-1 ms-2 name">
                {fullName}
              </div>
            </div>
          );
        },
      },

      // COMPANY
      {
        header: "Company",
        accessorKey: "company",
        enableColumnFilter: false,
        cell: (cell) => (
          <span>
            {cell.getValue() || "-"}
          </span>
        ),
      },

      // EMAIL
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
        cell: (cell) => (
          <span>
            {cell.getValue() || "-"}
          </span>
        ),
      },

      // PHONE
      {
        header: "Phone",
        accessorKey: "phone",
        enableColumnFilter: false,
        cell: (cell) => (
          <span>
            {cell.getValue() || "-"}
          </span>
        ),
      },

      // LOCATION
      {
        header: "Location",
        accessorKey: "lane",
        enableColumnFilter: false,
        cell: (cell) => (
          <span
            title={cell.getValue() || ""}
            style={{
              display: "inline-block",
              maxWidth: "220px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          >
            {cell.getValue() || "-"}
          </span>
        ),
      },

      // STATUS
      {
        header: "Status",
        accessorKey: "leadstatus",
        enableColumnFilter: false,
        cell: (cell) => (
          <span>
            {cell.getValue() || "-"}
          </span>
        ),
      },

      // CREATE DATE
      {
        header: "Create Date",
        accessorKey: "createdtime",
        enableColumnFilter: false,
        cell: (cell) => {

          const value = cell.getValue();

          if (!value) {
            return "-";
          }

          const date = new Date(value);

          if (Number.isNaN(date.getTime())) {
            return "-";
          }

          return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric"
          });
        },
      },

      // ACTION
      {
        header: "Action",
        cell: (cellProps) => {

          const leadData = cellProps.row.original;

          return (
            <ul className="list-inline hstack gap-2 mb-0">

              {/* CALL */}
              <li
                className="list-inline-item edit"
                title="Call"
              >
                <a
                  href={
                    leadData.phone
                      ? `tel:${leadData.phone}`
                      : "#"
                  }
                  className="text-muted d-inline-block"
                >
                  <i className="ri-phone-line fs-16"></i>
                </a>
              </li>

              {/* EMAIL */}
              <li
                className="list-inline-item edit"
                title="Email"
              >
                <a
                  href={
                    leadData.email
                      ? `mailto:${leadData.email}`
                      : "#"
                  }
                  className="text-muted d-inline-block"
                >
                  <i className="ri-mail-line fs-16"></i>
                </a>
              </li>

              {/* VIEW */}
              <li
                className="list-inline-item"
                title="View"
              >
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLeadView(leadData);
                  }}
                >
                  <i className="ri-eye-fill align-bottom text-muted"></i>
                </Link>
              </li>

              {/* EDIT */}
              <li
                className="list-inline-item"
                title="Edit"
              >
                <Link
                  className="edit-item-btn"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLeadClick(leadData);
                  }}
                >
                  <i className="ri-pencil-fill align-bottom text-muted"></i>
                </Link>
              </li>

              {/* DELETE */}
              <li
                className="list-inline-item"
                title="Delete"
              >
                <Link
                  className="remove-item-btn"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onClickDelete(leadData);
                  }}
                >
                  <i className="ri-delete-bin-fill align-bottom text-muted"></i>
                </Link>
              </li>

            </ul>
          );
        },
      },
    ],
    [
      checkedAll,
      handleLeadView,
      handleLeadClick,
      onClickDelete
    ]
  );

  const handleViewLeadPage = (leadData) => {

    if (!leadData?._id) {
      return;
    }

    setIsOverview(false);

    navigate(`/apps-crm-leads/view/${leadData._id}`, {
      state: {
        lead: leadData
      }
    });
  };

  const defaultdate = () => {
    let d = new Date(),
      months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return ((d.getDate() + ' ' + months[d.getMonth()] + ', ' + d.getFullYear()).toString());
  };

  const [date, setDate] = useState(defaultdate());

  const dateformate = (e) => {
    const date = e.toString().split(" ");
    const joinDate = (date[2] + " " + date[1] + ", " + date[3]).toString();
    setDate(joinDate);
  };

  document.title = "Leads | Velzon - React Admin & Dashboard Template";

  return (
    <React.Fragment>
      <div className="page-content">
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteLead}
          onCloseClick={() => setDeleteModal(false)}
        />
        <DeleteModal
          show={deleteModalMulti}
          onDeleteClick={() => {
            deleteMultiple();
            setDeleteModalMulti(false);
          }}
          onCloseClick={() => setDeleteModalMulti(false)}
        />

        <Container fluid>
          <div className="crm-mobile-page-title">
            <h5>Leads</h5>
          </div>

          <div className="crm-desktop-breadcrumb">
            <BreadCrumb title="Leads" pageTitle="CRM" />
          </div>
          <Row className="g-3 mb-4 crm-lead-stats">
            <Col md={6} xl={3}>
              <Card className="shadow-sm border-0 h-100">
                <CardBody>
                  <div className="d-flex align-items-center gap-3">
                    <div className="avatar-sm rounded-3 bg-soft-primary text-primary d-flex align-items-center justify-content-center">
                      <i className="ri-list-check fs-20"></i>
                    </div>
                    <div>
                      <p className="text-muted mb-1">Total Leads</p>
                      <h4 className="mb-0">{totalLeads}</h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="shadow-sm border-0 h-100">
                <CardBody>
                  <div className="d-flex align-items-center gap-3">
                    <div className="avatar-sm rounded-3 bg-soft-success text-success d-flex align-items-center justify-content-center">
                      <i className="ri-flashlight-line fs-20"></i>
                    </div>
                    <div>
                      <p className="text-muted mb-1">New Leads</p>
                      <h4 className="mb-0">{newLeadsCount}</h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="shadow-sm border-0 h-100">
                <CardBody>
                  <div className="d-flex align-items-center gap-3">
                    <div className="avatar-sm rounded-3 bg-soft-info text-info d-flex align-items-center justify-content-center">
                      <i className="ri-checkbox-circle-line fs-20"></i>
                    </div>
                    <div>
                      <p className="text-muted mb-1">Qualified Leads</p>
                      <h4 className="mb-0">{qualifiedLeadsCount}</h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="shadow-sm border-0 h-100">
                <CardBody>
                  <div className="d-flex align-items-center gap-3">
                    <div className="avatar-sm rounded-3 bg-soft-danger text-danger d-flex align-items-center justify-content-center">
                      <i className="ri-close-circle-line fs-20"></i>
                    </div>
                    <div>
                      <p className="text-muted mb-1">Lost Leads</p>
                      <h4 className="mb-0">{lostLeadsCount}</h4>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card id="leadsList" className="shadow-sm border-0">
                <CardHeader className="border-0 px-3 py-2 bg-white">
                  <Row className="g-4 align-items-center">
                    <Col sm={6} lg={4}>
                      <div className="search-box">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder="Search leads..."
                          value={leadSearch}
                          onChange={(e) => {
                            setLeadSearch(e.target.value);
                            setLeadPage(1);
                          }}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col className="ms-auto">
                      <div className="hstack gap-2 justify-content-end">
                        {isMultiDeleteButton && <button className="btn btn-soft-danger btn-sm"
                          onClick={() => setDeleteModalMulti(true)}
                        ><i className="ri-delete-bin-2-line me-1"></i> Delete</button>}
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => {

                            if (show) {

                              // Close filter and remove filter UI state
                              setShow(false);

                              navigate("/apps-crm-leads");

                            } else {

                              // Open filter and create browser history
                              setShow(true);

                              navigate("/apps-crm-leads?filter=open");

                            }

                          }}
                        >
                          <i className="ri-equalizer-line align-bottom me-1"></i>
                          Filters
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          id="create-btn"
                          onClick={() => {

                            setIsEdit(false);
                            setLead(null);
                            setDate(defaultdate());
                            setTag([]);
                            setAssignTag([]);
                            setModal(true);

                            navigate("/apps-crm-leads?create=open");

                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i> Add Lead
                        </button>
                      </div>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody className="pt-0">
                  {/* DESKTOP TABLE */}
                  <div className="crm-desktop-leads">
                    {isLeadsSuccess && Array.isArray(leads) && leads.length ? (
                      <TableContainer
                        columns={columns}
                        data={leadItems}
                        manualPagination={true}
                        pageCount={Math.ceil(leadsTotal / leadPageSize)}
                        pageIndex={leadPage - 1}
                        pageSize={leadPageSize}
                        totalRows={leadsTotal}
                        onPageChange={(nextPageIndex) => {
                          setLeadPage(nextPageIndex + 1);
                        }}
                        isGlobalFilter={false}
                        isLeadsFilter={false}
                      />
                    ) : (
                      <Loader error={error} />
                    )}
                  </div>


                  {/* MOBILE CARDS */}
                  <div className="crm-mobile-leads">

                    {isLeadsSuccess && leadItems.length > 0 ? (
                      leadItems.map((leadData) => (
                        <MobileLeadCard
                          key={leadData.id || leadData._id}
                          leadData={leadData}
                        />
                      ))
                    ) : isLeadsSuccess && leadItems.length === 0 ? (
                      <div className="crm-mobile-no-leads">
                        No leads found.
                      </div>
                    ) : (
                      <Loader error={error} />
                    )}

                  </div>

                  {/* MOBILE PAGINATION */}
                  {isLeadsSuccess && leadsTotal > leadPageSize && (
                    <MobileLeadPagination />
                  )}

                  <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
                    <ModalHeader className="bg-light p-3" toggle={toggle}>
                      {!!isEdit ? "Edit Lead" : "Add Lead"}
                    </ModalHeader>
                    <Form className="tablelist-form" onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}>
                      <ModalBody>
                        <Input type="hidden" id="id-field" />
                        <Row className="g-3">
                          <Col lg={12}>
                            <div className="text-center">
                              <div className="position-relative d-inline-block">
                                <div className="position-absolute bottom-0 end-0">
                                  <Label htmlFor="lead-image-input" className="mb-0">
                                    <div className="avatar-xs cursor-pointer">
                                      <div className="avatar-title bg-light border rounded-circle text-muted">
                                        <i className="ri-image-fill"></i>
                                      </div>
                                    </div>
                                  </Label>
                                  <Input className="form-control d-none" id="lead-image-input" type="file"
                                    accept="image/png, image/gif, image/jpeg"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.img || ""}
                                    invalid={
                                      validation.touched.img && validation.errors.img ? true : false
                                    }
                                  />
                                </div>
                                <div className="avatar-lg p-1">
                                  <div className="avatar-title bg-light rounded-circle">
                                    <img src={dummyImg} alt="dummyImg" id="lead-img" className="avatar-md rounded-circle object-fit-cover" />
                                  </div>
                                </div>
                              </div>
                              <h5 className="fs-13 mt-3">Lead Image</h5>
                            </div>
                            <div>
                              <Label
                                htmlFor="name-field"
                                className="form-label"
                              >
                                Name
                              </Label>
                              <Input
                                name="name"
                                id="customername-field"
                                className="form-control"
                                placeholder="Enter Name"
                                type="text"
                                validate={{
                                  required: { value: true },
                                }}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.name || ""}
                                invalid={
                                  validation.touched.name && validation.errors.name ? true : false
                                }
                              />
                              {validation.touched.name && validation.errors.name ? (
                                <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div>
                              <Label
                                htmlFor="email-field"
                                className="form-label"
                              >
                                Email
                              </Label>

                              <Input
                                name="email"
                                id="email-field"
                                className="form-control"
                                placeholder="Enter Email"
                                type="email"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.email || ""}
                                invalid={
                                  validation.touched.email &&
                                    validation.errors.email
                                    ? true
                                    : false
                                }
                              />

                              {validation.touched.email &&
                                validation.errors.email ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.email}
                                </FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                          
                          
                          <Col lg={6}>
                            <div>
                              <Label
                                htmlFor="phone-field"
                                className="form-label"
                              >
                                Phone
                              </Label>
                              <Input
                                name="phone"
                                id="phone-field"
                                className="form-control"
                                placeholder="Enter Phone Number"
                                type="text"
                                validate={{
                                  required: { value: true },
                                }}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.phone || ""}
                                invalid={
                                  validation.touched.phone && validation.errors.phone ? true : false
                                }
                              />
                              {validation.touched.phone && validation.errors.phone ? (
                                <FormFeedback type="invalid">{validation.errors.phone}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div>
                              <Label
                                htmlFor="leadstatus-field"
                                className="form-label"
                              >
                                Status
                              </Label>

                              <Input
                                type="select"
                                name="leadstatus"
                                id="leadstatus-field"
                                className="form-control"
                                value={validation.values.leadstatus || ""}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                              >
                                <option value="">Select Status</option>

                                {leadStatuses.map((status) => (
                                  <option
                                    key={status.value}
                                    value={status.value}
                                  >
                                    {status.label}
                                  </option>
                                ))}
                              </Input>
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div>
                              <Label
                                htmlFor="location-field"
                                className="form-label"
                              >
                                Location
                              </Label>
                              <Input
                                name="location"
                                id="location-field"
                                className="form-control"
                                placeholder="Enter Location"
                                type="text"
                                validate={{
                                  required: { value: true },
                                }}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.location || ""}
                                invalid={
                                  validation.touched.location && validation.errors.location ? true : false
                                }
                              />
                              {validation.touched.location && validation.errors.location ? (
                                <FormFeedback type="invalid">{validation.errors.location}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div>
                              <Label
                                htmlFor="course-field"
                                className="form-label"
                              >
                                Course
                              </Label>

                              <Input
                                name="course"
                                id="course-field"
                                className="form-control"
                                placeholder="Enter Course"
                                type="text"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.course || ""}
                              />
                            </div>
                          </Col>
                          <Col lg={12}>
                            <div>
                              <Label
                                htmlFor="date-field"
                                className="form-label"
                              >
                                Created Date
                              </Label>

                              <Flatpickr
                                name="date"
                                id="datepicker-publish-input"
                                className="form-control"
                                placeholder="Select a date"
                                options={{
                                  altInput: true,
                                  altFormat: "d M, Y",
                                  dateFormat: "d M, Y",
                                }}
                                onChange={(e) =>
                                  dateformate(e)
                                }
                                value={validation.values.date || ""}
                              />
                              {validation.touched.date && validation.errors.date ? (
                                <FormFeedback type="invalid">{validation.errors.date}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                      
                        </Row>
                      </ModalBody>
                      <ModalFooter>
                        <div className="hstack gap-2 justify-content-end">
                          <button
                            type="button"
                            className="btn btn-light"
                            onClick={() => {

                              setModal(false);
                              setIsEdit(false);
                              setLead(null);

                              navigate("/apps-crm-leads");

                            }}
                          >
                            Close
                          </button>
                          <button type="submit" className="btn btn-primary" id="add-btn"> {!!isEdit ? "Update" : "Add Lead"} </button>
                        </div>
                      </ModalFooter>
                    </Form>
                  </Modal>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <CrmFilter
        show={show}
        onCloseClick={() => {

          setShow(false);

          navigate("/apps-crm-leads");

        }}
        leadFilters={leadFilters}
        selectedCvid={selectedCvid}
        onCvidChange={handleApplyFilter}
        onApplyFilter={handleApplyFilter}
      />

      {/* Lead Overview - opens when clicking anywhere in the row */}
      <Modal
        isOpen={isOverview}
        toggle={toggleOverview}
        centered
        size="lg"
        className="lead-quick-view-modal"
      >
        <ModalHeader toggle={toggleOverview}>
          Lead Details
        </ModalHeader>

        <ModalBody>

          <div className="text-center mb-4">

            <div className="avatar-lg mx-auto mb-3">
              <div className="avatar-title rounded-circle bg-primary-subtle text-primary fs-2">
                {(
                  info?.name ||
                  info?.company ||
                  "L"
                ).charAt(0).toUpperCase()}
              </div>
            </div>

            <h4 className="mb-1">
              {info?.name || info?.company || "-"}
            </h4>

            <p className="text-muted mb-0">
              {info?.company || "-"}
            </p>

          </div>

          <Row className="g-3">

            <Col md={6}>
              <div className="border rounded p-3 h-100">
                <div className="text-muted small mb-1">
                  <i className="ri-user-line me-2"></i>
                  Name
                </div>

                <div className="fw-semibold">
                  {info?.name || "-"}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="border rounded p-3 h-100">
                <div className="text-muted small mb-1">
                  <i className="ri-building-line me-2"></i>
                  Company
                </div>

                <div className="fw-semibold">
                  {info?.company || "-"}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="border rounded p-3 h-100">
                <div className="text-muted small mb-1">
                  <i className="ri-mail-line me-2"></i>
                  Email
                </div>

                <div className="fw-semibold text-break">
                  {info?.email || "-"}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="border rounded p-3 h-100">
                <div className="text-muted small mb-1">
                  <i className="ri-phone-line me-2"></i>
                  Phone
                </div>

                <div className="fw-semibold">
                  {info?.phone || "-"}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="border rounded p-3 h-100">
                <div className="text-muted small mb-1">
                  <i className="ri-book-open-line me-2"></i>
                  Course
                </div>

                <div className="fw-semibold">
                  {info?.course || "-"}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="border rounded p-3 h-100">
                <div className="text-muted small mb-1">
                  <i className="ri-map-pin-line me-2"></i>
                  Location
                </div>

                <div className="fw-semibold">
                  {info?.location || "-"}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="border rounded p-3 h-100">
                <div className="text-muted small mb-1">
                  <i className="ri-user-star-line me-2"></i>
                  Assigned To
                </div>

                <div className="fw-semibold">
                  {info?.assignedTo || "-"}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="border rounded p-3 h-100">
                <div className="text-muted small mb-1">
                  <i className="ri-checkbox-circle-line me-2"></i>
                  Status
                </div>

                <div className="fw-semibold">
                  {info?.status || "-"}
                </div>
              </div>
            </Col>

          </Row>

        </ModalBody>

        <ModalFooter className="justify-content-between">

          <div className="d-flex gap-2">

            {info?.phone && (
              <Button
                color="success"
                outline
                onClick={() =>
                  window.open(`tel:${info.phone}`)
                }
                title="Call"
              >
                <i className="ri-phone-line me-1"></i>
                Call
              </Button>
            )}

            {info?.phone && (
              <Button
                color="success"
                outline
                onClick={() =>
                  window.open(
                    `https://wa.me/${info.phone.replace(/\D/g, "")}`,
                    "_blank"
                  )
                }
                title="WhatsApp"
              >
                <i className="ri-whatsapp-line me-1"></i>
                WhatsApp
              </Button>
            )}

            {info?.email && (
              <Button
                color="primary"
                outline
                onClick={() =>
                  window.open(`mailto:${info.email}`)
                }
                title="Email"
              >
                <i className="ri-mail-line me-1"></i>
                Email
              </Button>
            )}

          </div>

          <div className="d-flex gap-2">

            <Button
              color="secondary"
              onClick={toggleOverview}
            >
              Close
            </Button>

            <Button
              color="primary"
              onClick={() => {
                setLead(info);
                setIsEdit(true);
                toggleOverview();
                toggle();
              }}
            >
              <i className="ri-pencil-line me-1"></i>
              Edit Lead
            </Button>

            <Button
              color="primary"
              onClick={() => handleViewLeadPage(info)}
            >
              <i className="ri-eye-line me-1"></i>
              View Lead
            </Button>

          </div>

        </ModalFooter>

      </Modal>
    </React.Fragment >
  );
};

export default CrmLeads;